"use client";

import { z } from "zod";

import { Button } from "@/common/components-2/button";
import { MigrationLink } from "@/common/components-2/migration-link";
import { Section } from "@/common/components-2/section";
import { CmsSectionHeading } from "@/common/components-2/section-heading/cms-section-heading";
import type { HranoPage } from "@/payload-types";
import { imageSchema } from "@/services/cms/cms.schema";
import { EmitErrorReport } from "@/services/cms/components/emit-error-report";

import { Location, Speaker, Topic } from "../../components";

const upcomingSectionSchema = z.object({
  heading: z.string(),
  topic: z.object({
    title: z.string(),
    description: z.string(),
  }),
  location: z.object({
    title: z.string(),
    description: z.string(),
  }),
  heading2: z.string(),
  speakers: z.array(
    z.object({
      name: z.string(),
      image: imageSchema,
      description: z.string(),
      tags: z.array(z.object({ tag: z.string() })),
    }),
  ),
  button: z.object({
    text: z.string(),
    href: z.string(),
  }),
});

export function UpcomingSection(props: HranoPage["us"]) {
  let propsData: z.infer<typeof upcomingSectionSchema>;

  try {
    propsData = upcomingSectionSchema.parse(props);
  } catch (ex) {
    return <EmitErrorReport error={JSON.stringify(ex)} name="Upcoming section" />;
  }

  const { heading, topic, location, heading2, speakers, button } = propsData;

  return (
    <Section classes={{ section: "bg-white-linen-50 py-10 md:py-14 lg:py-20" }}>
      <CmsSectionHeading heading={heading} />
      <Topic title={topic.title} description={topic.description} className="mt-2 md:mt-6" />
      <Location title={location.title} description={location.description} className="mt-6" />
      <h3 className="mt-10 mb-8 text-center font-semibold text-2xl md:mt-14">{heading2}</h3>
      <div className="flex flex-col gap-6 md:flex-row">
        {speakers.map(({ name, description, image, tags }) => (
          <Speaker
            key={name}
            className="flex-1 md:max-w-[400px]"
            description={description}
            image={image}
            name={name}
            tags={tags.map(({ tag }) => tag)}
          />
        ))}
      </div>
      <Button className="mx-auto mt-10 md:mt-14 md:w-max lg:mt-16" asChild size="lg" variant="secondary">
        <MigrationLink href={button.href}>{button.text}</MigrationLink>
      </Button>
    </Section>
  );
}
